import clsx from 'clsx';
import type { SignalDotState } from '@arcanum-hq/shared-types';
import { Chip, type ChipVariant } from '@/components/ui/Chip';

// deal-signal-overhaul Phase 4 Session 4 (2026-05-12) modification.
//
// Two render-side changes per the Lovable parity decision:
//   1. The trailing `{n}% confidence` caption is REMOVED — Lovable spec
//      says confidence is NOT rendered as a visible percentage. Confidence
//      still flows through as a `data-confidence` attr for walkthrough
//      probes + as a sort key in `usePaginatedAssumptions`.
//   2. A 4-state signal-classification dot is rendered next to the source
//      chip when `signalDot != null` (parent computes via
//      `classifySignal(value, thresholds)`; today it's always `null` until
//      the `benchmark-data-seed` sister feature seeds per-input thresholds —
//      cards render without a dot rather than as a placeholder square).
//   3. The visible 5-label source badge maps from `ChipVariant` to USER /
//      MARKET / DEFAULT / INFERRED / — (was User / Market / System / Agent /
//      — pre-Phase-4). BENCHMARK is reserved for a future variant once
//      `provenance.benchmarkContext` data lands.
//   4. Optional `benchmarkContext` caption renders below the source-detail
//      line when present; omitted when undefined (no empty-state copy —
//      cards stay compact when benchmark data isn't seeded).

const SOURCE_LABEL: Record<ChipVariant, string> = {
  user: 'USER',
  market: 'MARKET',
  system: 'DEFAULT',
  agent: 'INFERRED',
  invalid: '—',
};

const SIGNAL_DOT_TOKEN: Record<SignalDotState, string> = {
  aligned: 'bg-accent',
  aggressive: 'bg-warn',
  conservative: 'bg-blue',
  differentiated: 'bg-purple',
};

// Hex for the left signal-bar (Tailwind classes don't propagate to box-shadow).
const SIGNAL_BAR_HEX: Record<SignalDotState, string> = {
  aligned: '#00c853', // @design-token resolves --color-accent
  aggressive: '#d4960f', // @design-token resolves --color-warn
  conservative: '#3a78b8', // @design-token resolves --color-blue
  differentiated: '#7860a8', // @design-token resolves --color-purple
};

export interface AssumptionCardProps {
  /** Schema name — stable React key + walkthrough probe. */
  namedRange: string;
  label: string;
  /** Pre-formatted value (caller controls dataType-aware formatting). */
  formattedValue: string;
  /**
   * 0..1 — diagnostic only. Exposed as `data-confidence` for walkthrough
   * probes. NEVER rendered as a visible `n% confidence` caption per the
   * Phase-4 Lovable-parity decision.
   */
  confidence: number;
  /**
   * Best-effort proxy for sensitivity-to-IRR — used by parent ranking only.
   * Surfaced as `data-sensitivity` for walkthrough probes.
   */
  sensitivityToIRR?: number;
  /** Source pill — drives chip color + glyph + visible 5-label badge. */
  source: ChipVariant;
  /** Optional source detail (e.g. "FRED 30Y · 2026-04-15") shown beneath. */
  sourceDetail?: string;
  /**
   * Phase 4 Session 4 — 4-state signal-classification dot next to the chip.
   * `null` when no benchmark thresholds are authored for this input (the
   * default state today until the `benchmark-data-seed` sister feature
   * lands). Cards render without a dot rather than as a placeholder square.
   */
  signalDot?: SignalDotState | null;
  /**
   * Phase 4 Session 4 — market-context line beneath source-detail when
   * present. Populated by Phase 6's `benchmark-data-seed` substrate via
   * `provenance.benchmarkContext`; omitted when undefined.
   */
  benchmarkContext?: string;
  className?: string;
}

/**
 * One row of the Assumption Integrity grid. Stable layout: label on top,
 * value + chip + optional signalDot on row two, optional source-detail
 * caption + optional benchmarkContext line on row three.
 */
export function AssumptionCard({
  namedRange,
  label,
  formattedValue,
  confidence,
  sensitivityToIRR,
  source,
  sourceDetail,
  signalDot = null,
  benchmarkContext,
  className,
}: AssumptionCardProps) {
  const signalHex = signalDot ? SIGNAL_BAR_HEX[signalDot] : '#586878'; // @design-token resolves --color-text-muted (muted fallback)
  return (
    <div
      data-testid="assumption-card"
      data-named-range={namedRange}
      data-confidence={confidence}
      data-sensitivity={sensitivityToIRR ?? 0}
      className={clsx(
        'relative flex flex-col gap-1 pl-3 pr-2.5 py-2 border border-panel-border rounded-[2px] bg-panel-bg overflow-hidden min-w-0',
        className
      )}
    >
      {/* Left signal bar — 3px wide, full height, glow when colored. */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: signalHex }}
      />
      <div className="flex items-baseline justify-between gap-2 min-w-0">
        <span
          className="text-[8.5px] font-mono text-text-muted uppercase tracking-[0.6px] truncate font-medium"
          title={label}
        >
          {label}
        </span>
        <Chip variant={source} label={SOURCE_LABEL[source]} />
      </div>
      <div className="flex items-baseline justify-between gap-2 min-w-0">
        <span
          data-testid="assumption-value"
          className="text-[13px] font-mono font-semibold tabular-nums tracking-[0.3px] text-text-bright truncate"
        >
          {formattedValue}
        </span>
        {signalDot && (
          <span
            data-testid="assumption-signal-dot"
            data-signal={signalDot}
            aria-label={`Signal: ${signalDot}`}
            className={clsx(
              'w-1.5 h-1.5 rounded-full shrink-0 self-center',
              SIGNAL_DOT_TOKEN[signalDot]
            )}
          />
        )}
      </div>
      {(sourceDetail || benchmarkContext) && (
        <div className="flex flex-col gap-0.5 text-[9px] font-mono leading-[1.35]">
          {sourceDetail && (
            <span
              data-testid="assumption-source-detail"
              className="text-text-muted line-clamp-2"
              title={sourceDetail}
            >
              {sourceDetail}
            </span>
          )}
          {benchmarkContext && (
            <span
              data-testid="assumption-benchmark-context"
              className="text-text-dim line-clamp-2"
              title={benchmarkContext}
            >
              {benchmarkContext}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
